const DEFAULT_COLLECTION_FORMAT = "hardiness-zone-{zone}";

class DynamicZoneCollection extends HTMLElement {
	static get observedAttributes() {
		return ["data-collection-handle-format", "data-products-limit", "data-fallback-to-default-hardiness-zone"];
	}

	constructor() {
		super();
		this.storageKey = "locator";
		this.enableProductDiscount = false;
		for (let i = 1; i <= 5; i++) {
			this[`discountCondition${i}Tags`] = "";
			this[`discountCondition${i}Percentage`] = 0;
		}
		this.currentZone = null;
		this.currentIndex = 0;
		this.isTransitioning = false;
		this.startX = 0;
		this.currentX = 0;
		this.isDragging = false;
		this.metafieldsData = {};
		this.usingFallbackCollection = false;
		this.readSettings();
	}

	readSettings() {
		this.productsLimit = parseInt(this.dataset.productsLimit, 10) || 8;
		this.cardsDesktop = parseInt(this.dataset.cardsDesktop, 10) || 4;
		this.cardsMobile = parseInt(this.dataset.cardsMobile, 10) || 2;
		this.sectionTitle = this.dataset.sectionTitle || "Recommended for Your Zone {zone} !";
		this.shopAllText = this.dataset.shopAllText || "Shop All";
		this.sectionId = this.dataset.sectionId || "";
		this.collectionHandleFormat = this.dataset.collectionHandleFormat || DEFAULT_COLLECTION_FORMAT;
		this.fallbackToDefaultHardinessZone = this.dataset.fallbackToDefaultHardinessZone === "true";
		this.isCarousel = this.dataset.cardCarousel === "true";
		this.hasHover = this.dataset.hoverImage === "true";
	}

	normalizeZone(zone) {
		return zone != null && zone !== "" ? String(zone) : null;
	}

	buildCollectionHandleFromFormat(zone, format) {
		const zoneStr = this.normalizeZone(zone);
		if (!zoneStr) return "";
		return format.replace(/\{zone\}/g, zoneStr);
	}

	buildCollectionHandle(zone) {
		return this.buildCollectionHandleFromFormat(zone, this.collectionHandleFormat);
	}

	buildDefaultCollectionHandle(zone) {
		return this.buildCollectionHandleFromFormat(zone, DEFAULT_COLLECTION_FORMAT);
	}

	isUnsupportedZone(zone) {
		const zoneStr = this.normalizeZone(zone);
		return zoneStr != null && ["1", "2", "12"].includes(zoneStr);
	}

	getSectionRoot() {
		return this.closest(".section-zone-collection") || this.closest("section");
	}

	loadMetafieldsData() {
		const metafieldsScript = document.getElementById(`zone-products-metafields-${this.sectionId}`);
		if (metafieldsScript) {
			try {
				this.metafieldsData = JSON.parse(metafieldsScript.textContent);
			} catch (error) {
				console.error("Error parsing metafields data:", error);
				this.metafieldsData = {};
			}
		}
	}

	getProductMetafields(zone, productHandle) {
		const zoneStr = this.normalizeZone(zone);
		if (!zoneStr) return {};

		if (this.usingFallbackCollection) {
			const fallbackData = this.metafieldsData.fallback?.[zoneStr]?.[productHandle];
			if (fallbackData) return fallbackData;
		}

		if (this.metafieldsData[zoneStr]?.[productHandle]) {
			return this.metafieldsData[zoneStr][productHandle];
		}

		return {};
	}

	/** Reads tag discount rules from `layout/theme.liquid` → `window.productDiscountSettings`. */
	loadDiscountSettings() {
		const s = typeof window !== "undefined" ? window.productDiscountSettings : null;
		if (!s || typeof s !== "object") return;
		this.enableProductDiscount = Boolean(s.enableProductDiscount);
		this.countryCode = s.countryCode ? String(s.countryCode).toUpperCase() : "";
		const conditions = Array.isArray(s.conditions) ? s.conditions : [];
		for (let i = 1; i <= 5; i++) {
			const c = conditions[i - 1];
			const tags = c?.tags;
			this[`discountCondition${i}Tags`] =
				tags != null && tags !== "" ? (typeof tags === "string" ? tags : String(tags)) : "";
			const rawPct = c?.discount != null ? Number(c.discount) : NaN;
			this[`discountCondition${i}Percentage`] = Number.isFinite(rawPct) ? rawPct : 0;
			const rawAmtUsa = c?.amountUsa != null ? Number(c.amountUsa) : NaN;
			this[`discountCondition${i}AmountUsa`] = Number.isFinite(rawAmtUsa) ? rawAmtUsa : 0;
			const rawAmtCa = c?.amountCanada != null ? Number(c.amountCanada) : NaN;
			this[`discountCondition${i}AmountCanada`] = Number.isFinite(rawAmtCa) ? rawAmtCa : 0;
		}
	}

	/** Same rules as `snippets/price.liquid`: tag conditions 1–5, skip real compare_at sales. */
	applyCustomDiscount(product, priceNum, compareNum) {
		if (!this.enableProductDiscount) return { price: priceNum, compareAt: compareNum };
		if (compareNum != null && compareNum > priceNum) return { price: priceNum, compareAt: compareNum };
		const rawTags = product?.tags;
		const tagParts = Array.isArray(rawTags)
			? rawTags
			: typeof rawTags === "string"
				? rawTags.split(",")
				: [];
		const productTags = tagParts.map((t) => String(t).toLowerCase().trim()).filter(Boolean);
		const isCanada = this.countryCode === "CA";

		for (let i = 1; i <= 5; i++) {
			const tagsStr = this[`discountCondition${i}Tags`];
			if (!tagsStr || !String(tagsStr).trim()) continue;
			const conditionTags = String(tagsStr)
				.split(",")
				.map((t) => t.toLowerCase().trim())
				.filter(Boolean);
			if (!productTags.some((pt) => conditionTags.includes(pt))) continue;

			const pct = this[`discountCondition${i}Percentage`] || 0;
			const amtUsa = this[`discountCondition${i}AmountUsa`] || 0;
			const amtCa = this[`discountCondition${i}AmountCanada`] || 0;

			if (pct > 0) {
				return { price: priceNum - (priceNum * pct) / 100, compareAt: priceNum };
			}

			let appliedAmt = amtUsa;
			if (isCanada && amtCa > 0) {
				appliedAmt = amtCa;
			}

			if (appliedAmt > 0) {
				const discountedPrice = Math.max(0, priceNum - appliedAmt);
				return { price: discountedPrice, compareAt: priceNum };
			}

			return { price: priceNum, compareAt: compareNum };
		}
		return { price: priceNum, compareAt: compareNum };
	}

	getZoneFromStorage() {
		const raw = localStorage.getItem(this.storageKey);
		if (!raw) return null;
		try {
			return this.normalizeZone(JSON.parse(raw).zone);
		} catch {
			return null;
		}
	}

	attributeChangedCallback(name) {
		if (
			name === "data-collection-handle-format" ||
			name === "data-products-limit" ||
			name === "data-fallback-to-default-hardiness-zone"
		) {
			this.readSettings();
			this.loadMetafieldsData();
			this.loadZoneProducts();
		}
	}

	connectedCallback() {
		this.readSettings();
		this.loadDiscountSettings();
		this.loadMetafieldsData();
		this.render();
		this.loadZoneProducts();

		this.setupStorageListener();
		this.setupCarouselListeners();
		this.setupQuickAddListeners();

		document.addEventListener("zoneUpdated", (e) => {
			if (e.detail?.zone) this.handleZoneUpdate(e.detail.zone);
		});

		window.addEventListener("resize", () => {
			this.updateCarousel();
			this.updateButtons();
		});
	}

	setupStorageListener() {
		window.addEventListener("storage", (e) => {
			if (e.key === this.storageKey && e.newValue) {
				this.handleZoneUpdate(JSON.parse(e.newValue).zone);
			}
		});

		this.storageCheckInterval = setInterval(() => {
			const zone = this.getZoneFromStorage();
			if (zone && zone !== this.currentZone) this.handleZoneUpdate(zone);
		}, 1000);
	}

	disconnectedCallback() {
		if (this.storageCheckInterval) {
			clearInterval(this.storageCheckInterval);
		}
	}

	handleZoneUpdate(newZone) {
		const zoneStr = this.normalizeZone(newZone);
		if (zoneStr && zoneStr !== this.currentZone) {
			this.currentZone = zoneStr;
			this.loadZoneProducts();
		}
	}

	/** Opens the same hardiness zone popup as "Set my Zone" / "Update my Hardiness Zone" in zone-filter */
	openZonePopup(e) {
		if (e) e.preventDefault();
		const trigger = document.querySelector('tool-tip-trigger.location-zone-popup, tool-tip-trigger[data-tool-tip="LocationDetector"]');
		if (!trigger) return;
		const contentEl = trigger.querySelector('[data-tool-tip-trigger-content]');
		trigger.classList.add('is-open');
		document.dispatchEvent(new CustomEvent('tooltip:open', {
			detail: {
				context: trigger.getAttribute('data-tool-tip') || 'LocationDetector',
				content: contentEl ? contentEl.innerHTML : '',
				tool_tip_classes: trigger.getAttribute('data-tool-tip-classes') || 'location-add-grow-zone'
			},
			bubbles: true
		}));
	}

	boundZonePopupClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.openZonePopup(e);
	};

	render() {
		const gridClass = "product-grid product-grid--grid product-grid--zone-collection product-grid--" + this.sectionId;
		this.innerHTML = `
      <div class="zone-products-scroll-container">
        <ul class="${gridClass}" data-testid="product-grid" product-grid-view="default" ref="grid" role="list" data-product-card-size="medium" style="--mobile-columns: ${this.cardsMobile}; --zone-cards-desktop: ${this.cardsDesktop};">
          <li class="loading-products" style="list-style: none; grid-column: 1 / -1;">
            <p>Loading products...</p>
          </li>
        </ul>
      </div>
    `;
	}

	async loadZoneProducts() {
		this.readSettings();
		this.loadMetafieldsData();

		try {
			let zone = this.getZoneFromStorage();
			if (!zone) {
				await this.waitForZoneDetection();
				zone = this.getZoneFromStorage();
			}

			if (!zone || this.isUnsupportedZone(zone)) {
				this.hide();
				return;
			}

			await this.fetchAndDisplayProducts(zone);
		} catch {
			this.hide();
		}
	}

	async waitForZoneDetection(maxWait = 5000) {
		const start = Date.now();
		return new Promise((resolve) => {
			const id = setInterval(() => {
				if (localStorage.getItem(this.storageKey) || Date.now() - start > maxWait) {
					clearInterval(id);
					resolve();
				}
			}, 100);
		});
	}

	async fetchAvailableProducts(collectionHandle) {
		if (!collectionHandle) return [];

		const fetchLimit = Math.max(this.productsLimit * 3, 24);
		const collectionUrl = `/collections/${collectionHandle}/products.json?limit=${fetchLimit}`;

		try {
			const response = await fetch(collectionUrl);
			if (!response.ok) return [];

			const data = await response.json();
			return (data.products || []).filter((product) =>
				product.variants?.some((variant) => variant.available)
			);
		} catch {
			return [];
		}
	}

	async resolveProductsForZone(zoneStr) {
		const primaryHandle = this.buildCollectionHandle(zoneStr);
		const primaryProducts = await this.fetchAvailableProducts(primaryHandle);

		if (primaryProducts.length > 0) {
			return {
				products: primaryProducts,
				collectionHandle: primaryHandle,
				usingFallback: false,
			};
		}

		if (!this.fallbackToDefaultHardinessZone) {
			return { products: [], collectionHandle: primaryHandle, usingFallback: false };
		}

		const defaultHandle = this.buildDefaultCollectionHandle(zoneStr);
		if (defaultHandle === primaryHandle) {
			return { products: [], collectionHandle: primaryHandle, usingFallback: false };
		}

		const fallbackProducts = await this.fetchAvailableProducts(defaultHandle);
		return {
			products: fallbackProducts,
			collectionHandle: defaultHandle,
			usingFallback: fallbackProducts.length > 0,
		};
	}

	async fetchAndDisplayProducts(zone) {
		const zoneStr = this.normalizeZone(zone);
		if (!zoneStr) {
			this.hide();
			return;
		}

		this.currentZone = zoneStr;

		const productsGrid = this.querySelector(".product-grid");
		if (productsGrid) {
			productsGrid.innerHTML = '<li class="loading-products" style="list-style: none; grid-column: 1 / -1;"><p>Loading products...</p></li>';
		}

		const { products, collectionHandle, usingFallback } = await this.resolveProductsForZone(zoneStr);
		this.usingFallbackCollection = usingFallback;

		if (products.length === 0) {
			this.hide();
			return;
		}

		const productsToDisplay = products.slice(0, this.productsLimit);
		this.displayProducts(productsToDisplay, zoneStr, collectionHandle);
		this.show();
	}

	displayProducts(products, zone, collectionHandle) {
		const titleEl = this.querySelector(".zone-collection-title");
		const shopAllLink = this.querySelector(".zone-shop-all-link");
		const productsGrid = this.querySelector(".product-grid");

		if (titleEl) {
			titleEl.innerHTML = this.sectionTitle.replace("Zone {zone}", `<a href="#" class="zone-link" data-zone="${zone}">Zone ${zone}</a>`);
			titleEl.querySelector('.zone-link')?.addEventListener('click', this.boundZonePopupClick);
		}

		const wrapper = this.closest(".zone-collection-wrapper");
		wrapper?.querySelectorAll("[data-zone-heading-placeholder]").forEach((el) => {
			el.innerHTML = `<a href="#" class="zone-link-open-popup" aria-label="Update my Hardiness Zone">Zone ${zone}</a>`;
			el.querySelector("a")?.addEventListener("click", this.boundZonePopupClick);
		});

		const promotionalTextEl = wrapper?.querySelector(".zone-promotional-text");
		if (promotionalTextEl) {
			const zonePlaceholders = promotionalTextEl.querySelectorAll(".zone-dynamic-number");
			if (zonePlaceholders.length) {
				zonePlaceholders.forEach((el) => {
					const link = document.createElement('a');
					link.href = '#';
					link.className = 'zone-link-open-popup';
					link.textContent = 'Zone ' + zone;
					link.setAttribute('aria-label', 'Update my Hardiness Zone');
					el.parentNode?.replaceChild(link, el);
					link.addEventListener('click', this.boundZonePopupClick);
				});
			} else {
				const existingLinks = promotionalTextEl.querySelectorAll('.zone-link-open-popup');
				if (existingLinks.length) {
					existingLinks.forEach((link) => { link.textContent = 'Zone ' + zone; });
				} else if (/Zone\s*\d+/i.test(promotionalTextEl.innerHTML)) {
					promotionalTextEl.innerHTML = promotionalTextEl.innerHTML.replace(/Zone\s*\d+/i, `<a href="#" class="zone-link-open-popup" aria-label="Update my Hardiness Zone">Zone ${zone}</a>`);
					promotionalTextEl.querySelectorAll('.zone-link-open-popup').forEach((link) => link.addEventListener('click', this.boundZonePopupClick));
				}
			}
		}

		// Update VIEW ALL button link to zone collection (dynamic)
		const promotionalButton = wrapper?.querySelector(".zone-promotional-button");
		if (promotionalButton && promotionalButton instanceof HTMLAnchorElement) {
			promotionalButton.href = `/collections/${collectionHandle}`;
		}

		if (shopAllLink) {
			shopAllLink.href = `/collections/${collectionHandle}`;
			shopAllLink.classList.remove("hide");
		}
		if (productsGrid) {
			const productsHTML = products.map((product, index) => this.createProductCard(product, zone, index)).join("");
			productsGrid.innerHTML = productsHTML;
		}

		// Reset carousel state and update
		this.currentIndex = 0;
		// Use setTimeout to ensure DOM is updated before calculating positions
		setTimeout(() => {
			this.updateCarousel();
			this.updateButtons();
		}, 0);
	}

	createProductCard(product, zone, index) {
		const variant = product.variants[0];
		const firstAvailableVariant = product.variants.find((v) => v.available) || product.variants[0];
		const rawPrice = variant?.price != null ? parseFloat(variant.price) : 0;
		const rawCompare = variant?.compare_at_price != null ? parseFloat(variant.compare_at_price) : null;
		const { price: priceNum, compareAt: compareNum } = this.applyCustomDiscount(product, rawPrice, rawCompare);
		const priceFormatted = this.formatMoney(priceNum);
		const comparePriceFormatted = compareNum != null ? this.formatMoney(compareNum) : null;
		const onSale = compareNum != null && compareNum > priceNum;
		const isAvailable = product.variants.some((v) => v.available);
		const productUrl = variant && variant.id ? `/products/${product.handle}?variant=${variant.id}` : `/products/${product.handle}`;
		const title = product.title.replace(/"/g, "&quot;");
		const imageAlt = product.title.replace(/"/g, "&quot;");
		const variantId = firstAvailableVariant && firstAvailableVariant.id ? firstAvailableVariant.id : "";

		const metafields = this.getProductMetafields(zone, product.handle);
		const tagText = metafields.product_tag || "";
		const bgColor = metafields.product_tag_background_color || "";
		const textColor = metafields.product_tag_text_color || "";
		const productType = metafields.product_type || "";

		const maxVisibleSlides = this.isCarousel ? 5 : (this.hasHover ? 2 : 1);
		const imagesToRender = (product.images || []).slice(0, maxVisibleSlides);
		const arrowIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>`;
		const pointerEvents = this.hasHover ? `on:pointerenter="/previewImage" on:pointerleave="/resetImage"` : "";

		let galleryHTML = '';
		if (imagesToRender.length > 0) {
			const slidesHTML = imagesToRender.map((img, i) => {
				const isSelected = i === 0;
				const loadingAttr = (index < 5 && i === 0) ? 'eager' : 'lazy';
				const imgSrc = typeof img === 'string' ? img : (img.src || '');
				const imgId = typeof img === 'object' && img.id ? img.id : i;
				return `
				<slideshow-slide ref="slides[]" class="product-media-container product-media-container--image media-fit" aria-hidden="${!isSelected}" style="--slideshow-timeline: --slide-${i}; --product-media-fit: cover; --grid-template-rows: 50dvh 1fr; --grid-template-rows-desktop: 70dvh 1fr;" slide-id="slide-${imgId}">
					<img ref="imagesToTransition[]" src="${imgSrc}" loading="${loadingAttr}" class="grid__image-contain" alt="${imageAlt}" sizes="(min-width: 750px) 25vw, 50vw">
				</slideshow-slide>
				`;
			}).join('');

			const showArrows = this.isCarousel && imagesToRender.length > 1;

			galleryHTML = `
			<div class="card-gallery border-style" data-product-id="${product.id}" style="--gallery-aspect-ratio: 1;" data-image-ratio="square">
				<a class="contents" href="${productUrl}" aria-label="${title}" ref="cardGalleryLink">
					<slideshow-component ref="slideshow" style="--slideshow-timeline: ${imagesToRender.map((_, i) => `--slide-${i}`).join(', ')}; --slideshow-align-items: flex-start; --slide-min-height: auto; --slide-min-height-desktop: auto;" initial-slide="0" infinite>
						<slideshow-container ref="slideshowContainer">
							${showArrows ? `
							<slideshow-arrows position="center">
								<button ref="previous" class="slideshow-control slideshow-control--previous button button-unstyled button-unstyled--transparent flip-x" aria-label="Previous slide" on:click="/previous" type="button">
									<span class="svg-wrapper icon-arrow">${arrowIcon}</span>
								</button>
								<button ref="next" class="slideshow-control slideshow-control--next button button-unstyled button-unstyled--transparent" aria-label="Next slide" on:click="/next" type="button">
									<span class="svg-wrapper icon-arrow">${arrowIcon}</span>
								</button>
							</slideshow-arrows>
							` : ''}
							<slideshow-slides tabindex="-1" ref="scroller">
								${slidesHTML}
							</slideshow-slides>
						</slideshow-container>
					</slideshow-component>
				</a>
			</div>
			`;
		}

		return `
<li class="product-grid__item product-grid__item--${index}" data-product-id="${product.id}" ref="cards[]">
  <product-card class="product-card" data-product-id="${product.id}">
  <a href="${productUrl}" class="product-card__link" ref="productCardLink"><span class="visually-hidden">${title}</span></a>
  <div class="grid-item grid-product zone-grid-product">
    <div class="product-grid-item" data-product-handle="${product.handle}" data-product-id="${product.id}">
      <div class="grid-item__content">
        <div class="grid-item__link" style="cursor: pointer;">
          <div class="grid-product__image-wrap zone-card-gallery" ${pointerEvents}>
            ${galleryHTML}
            ${!isAvailable ? '<div class="grid-product__tag grid-product__tag--sold-out">Sold Out</div>' : ""}
            ${tagText ? `<span class="plant-recommendation-tag product-badge" style="${bgColor ? `--tag-bg: ${bgColor};` : ""} ${textColor ? `--tag-text: ${textColor};` : ""}">${tagText}</span>` : ""}
            ${isAvailable && variantId ? `<div class="zone-quick-add-overlay" aria-hidden="true"><button type="button" class="zone-quick-add-btn" data-variant-id="${variantId}" data-product-title="${title}">Add to bag</button></div>` : ""}
          </div>
          <div class="grid-item__meta">
            <div class="grid-item__meta-main">
			${productType ? `<span class="product-type">${productType.replace(/"/g, "&quot;")}</span>` : ""}
              <div class="grid-product__title">${product.title.replace(/"/g, "&quot;")}</div>
              <div class="grid-product__price">
                <span class="grid-product__price--current">${priceFormatted}</span>${onSale && isAvailable ? ` <span class="grid-product__price--original">${comparePriceFormatted}</span>` : ""}
              </div>
            </div>
            <div class="grid-item__meta-secondary">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </product-card>
</li>
    `.trim();
	}

	formatMoney(cents) {
		const dollars = cents.toFixed(2);
		const num = parseFloat(dollars);
		const display = Number.isInteger(num) ? String(num) : dollars;
		return `$${display}`;
	}

	setupCarouselListeners() {
		const prevButton = this.querySelector(".zone-collection-prev");
		const nextButton = this.querySelector(".zone-collection-next");
		const track = this.querySelector(".product-grid");

		if (prevButton) {
			prevButton.addEventListener("click", () => this.prev());
		}
		if (nextButton) {
			nextButton.addEventListener("click", () => this.next());
		}

		if (track) {
			track.addEventListener("touchstart", (e) => this.handleTouchStart(e), { passive: true });
			track.addEventListener("touchmove", (e) => this.handleTouchMove(e), { passive: true });
			track.addEventListener("touchend", () => this.handleTouchEnd(), { passive: true });

			track.addEventListener("mousedown", (e) => this.handleMouseDown(e));
			track.addEventListener("mousemove", (e) => this.handleMouseMove(e));
			track.addEventListener("mouseup", () => this.handleMouseUp());
			track.addEventListener("mouseleave", () => this.handleMouseUp());
		}
	}

	setupQuickAddListeners() {
		this.addEventListener("click", (e) => {
			const btn = e.target.closest(".zone-quick-add-btn");
			if (!btn || btn.disabled) return;
			e.preventDefault();
			e.stopPropagation();
			const variantId = btn.getAttribute("data-variant-id");
			if (!variantId) return;
			this.handleQuickAdd(btn, variantId);
		});
	}

	async handleQuickAdd(btn, variantId) {
		const label = btn.textContent;
		btn.disabled = true;
		btn.textContent = "Adding…";
		const reset = (text, delay = 2000) => {
			setTimeout(() => { btn.textContent = text; btn.disabled = false; }, delay);
		};
		const ZoneQuickAdd = window.ZoneQuickAdd;
		if (!ZoneQuickAdd?.addToCart) {
			btn.textContent = "Error";
			reset(label);
			return;
		}
		try {
			const result = await ZoneQuickAdd.addToCart(variantId, 1);
			btn.textContent = result.success ? "Added" : (result.error || "Error");
			reset(label, result.success ? 1500 : 2000);
		} catch {
			btn.textContent = "Error";
			reset(label);
		}
	}

	getVisibleCards() {
		return window.innerWidth <= 749 ? this.cardsMobile : this.cardsDesktop;
	}

	getMaxIndex() {
		const grid = this.querySelector(".product-grid");
		if (!grid) return 0;
		return Math.max(0, grid.querySelectorAll(".product-grid__item").length - this.getVisibleCards());
	}

	prev() {
		if (this.isTransitioning) return;
		this.currentIndex = Math.max(0, this.currentIndex - 1);
		this.updateCarousel();
	}

	next() {
		if (this.isTransitioning) return;
		this.currentIndex = Math.min(this.getMaxIndex(), this.currentIndex + 1);
		this.updateCarousel();
	}

	updateCarousel() {
		const grid = this.querySelector(".product-grid");
		if (!grid || !grid.querySelectorAll(".product-grid__item").length) return;
		this.isTransitioning = true;
		this.updateButtons();
		setTimeout(() => { this.isTransitioning = false; }, 300);
	}

	updateButtons() {
		const prevButton = this.querySelector(".zone-collection-prev");
		const nextButton = this.querySelector(".zone-collection-next");

		if (!prevButton || !nextButton) return;

		prevButton.disabled = this.currentIndex === 0;
		nextButton.disabled = this.currentIndex >= this.getMaxIndex();

		prevButton.style.opacity = prevButton.disabled ? "0.5" : "1";
		nextButton.style.opacity = nextButton.disabled ? "0.5" : "1";
	}

	handleTouchStart(e) {
		this.startX = e.touches[0].clientX;
		this.isDragging = true;
	}

	handleTouchMove(e) {
		if (!this.isDragging) return;
		this.currentX = e.touches[0].clientX;
	}

	handleTouchEnd() {
		if (!this.isDragging) return;
		this.isDragging = false;
		this.handleSwipeEnd(this.startX - this.currentX);
	}

	handleMouseDown(e) {
		this.startX = e.clientX;
		this.isDragging = true;
		const track = this.querySelector(".product-grid");
		if (track) track.style.cursor = "grabbing";
		e.preventDefault();
	}

	handleMouseMove(e) {
		if (this.isDragging) this.currentX = e.clientX;
	}

	handleMouseUp() {
		if (!this.isDragging) return;
		this.isDragging = false;
		const track = this.querySelector(".product-grid");
		if (track) track.style.cursor = "grab";
		this.handleSwipeEnd(this.startX - this.currentX);
	}

	handleSwipeEnd(deltaX) {
		if (Math.abs(deltaX) <= 50) return;
		deltaX > 0 ? this.next() : this.prev();
	}

	showError(message) {
		const productsGrid = this.querySelector(".product-grid");
		if (productsGrid) {
			productsGrid.innerHTML = `<li class="error-message" style="list-style: none; grid-column: 1 / -1;"><p>${message}</p></li>`;
		}
	}

	hide() {
		const sectionRoot = this.getSectionRoot();
		if (sectionRoot) {
			sectionRoot.classList.remove("is-zone-collection-visible");
			sectionRoot.style.display = "none";
			sectionRoot.setAttribute("aria-hidden", "true");
		}
	}

	show() {
		const sectionRoot = this.getSectionRoot();
		if (sectionRoot) {
			sectionRoot.classList.add("is-zone-collection-visible");
			sectionRoot.style.display = "";
			sectionRoot.removeAttribute("aria-hidden");
		}
	}
}

customElements.define("dynamic-zone-collection", DynamicZoneCollection);

document.addEventListener("shopify:section:load", (event) => {
	const element = event.target?.querySelector?.("dynamic-zone-collection");
	if (!element) return;
	element.readSettings();
	element.loadMetafieldsData();
	element.loadZoneProducts();
});